import { FC, useEffect, useState } from 'react';
import * as yup from 'yup';
import useHandleErrors from '../../../hooks/useHandleErrors';
import { InvoiceService } from '../../../services/invoices/invoiceService';
import useCompanies from '../../../hooks/api-calls/useCompanies';
import moment from 'moment';
import { useFormik } from 'formik';
import { toast } from 'sonner';
import { Button, Modal } from 'rizzui';
import DateField from '../../../components/forms/DateField';

interface ExportByDatesModalProps {
  isOpen: boolean;
  onClose: () => void;
  type?: 'proforma' | 'invoice';
}


const schema = yup.object().shape({
    dateRange: yup.array().of(yup.date()).required('Selecciona una fecha'),
});

const ExportByDatesModal: FC<ExportByDatesModalProps> = ({ isOpen, onClose, type = 'proforma' }) => {
    
    // STATES
    const [ submitting, setSubmitting ] = useState<boolean>(false);

    const isProforma = type === 'proforma';

    // HOOKS

    const { handleErrors } = useHandleErrors();
    const service = new InvoiceService();
    const { getCompaniesList } = useCompanies();


    // FORMIK

    const formik = useFormik({
        initialValues: {
            dateRange: [],
        },
        validationSchema: schema,
        onSubmit: async (values) => {
            exportMultiByDateRange(
                values.dateRange
            );

            onClose();
        },
    });





    // FUNCTIONS

    // -------------------------------------------------------------------------------------------------------------
    /**
     * @ES DESCARGAR FACTURAS PROVENIENTE BLOB EN FORMATO ZIP
     * @EN DOWNLOAD INVOICES FROM BLOB IN ZIP FORMAT
     */
    // -------------------------------------------------------------------------------------------------------------
    const exportMultiByDateRange = async (dateRange: any) => {
        try {
            // Transformar el array [startDate, endDate, selectedDateField] a objeto {from, to}
            const dateRangeObject = {
                from: dateRange[0],
                to: dateRange[1],
                is_paid: !isProforma
            };
            
            const response = await service.exportInvoicesByDateToPDF(dateRangeObject);
            
            if (response && response.getResponseData()) {
                const fileData = response.getResponseData();
                
                // Verificar que los datos no estén vacíos
                if (fileData) {
                    // Crear un blob con el tipo MIME para archivos ZIP
                    const blob = new Blob([fileData], { type: 'application/zip' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                
                    // Formatear fechas de Y-m-d a d-m-Y para el nombre del archivo
                const formatDateForFileName = (dateStr: string) => {
                    const [year, month, day] = dateStr.split('-');
                    return `${day}-${month}-${year}`;
                };
                
                const fromFormatted = formatDateForFileName(dateRangeObject.from);
                const toFormatted = formatDateForFileName(dateRangeObject.to);
                const fileName = `${isProforma ? 'proformas' : 'facturas'}_clientes_${fromFormatted}_${toFormatted}.zip`;
                    link.setAttribute('download', fileName);
                    
                    // Descargar el archivo
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    
                    toast.success(`${isProforma ? 'Proformas' : 'Facturas'} descargadas correctamente en formato ZIP`);
                } else {
                    toast.error(`No se pudieron obtener los archivos de ${isProforma ? 'proformas' : 'facturas'}`);
                }
            } else {
                toast.error(`Error al descargar las ${isProforma ? 'proformas' : 'facturas'}`);
            }
        } catch (error: any) {
            toast.error(`Error al descargar las ${isProforma ? 'proformas' : 'facturas'}: ${error.message || 'Error desconocido'}`);
            handleErrors(error);
        }
    };
    // -------------------------------------------------------------------------------------------------------------

    useEffect(() => {
        formik.resetForm();
    }, [isOpen]);

    // RENDER

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            overlayClassName="backdrop-blur"
            containerClassName='!shadow-xl'
            className='z-[9999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-8'>
                <div className='mb-7 flex items-center justify-between'>
                    <h2 className="text-xl font-semibold">Generar PDF de {isProforma ? 'proformas' : 'facturas'} en un rango de fechas</h2>
                </div>

                <div className='mb-6'>
                    <form onSubmit={formik.handleSubmit}>
                        <div className='mb-4'>
                            <label htmlFor="dateRange" className="text-sm font-medium text-gray-700">Rango de fechas</label>
                            <div className="mt-1">
                                <DateField
                                    id="dateRange"
                                    name="dateRange"
                                    value={formik.values.dateRange}
                                    onChange={(date:any) =>
                                        formik.setFieldValue('dateRange', date)
                                    }
                                    onBlur={formik.handleBlur}
                                    className="w-full rounded-md w-100"
                                />
                            </div>
                        </div>
                    </form>
                   
                </div>

                <div className='flex gap-4 justify-end'>
                    <Button 
                        variant="outline"
                        onClick={onClose}
                        color="secondary"
                    >
                        Cancelar
                    </Button>


                    <Button
                        type="submit"
                        color="primary"
                        onClick={() => formik.handleSubmit()}
                        className="w-full"
                        disabled={submitting || !formik.isValid}
                        isLoading={submitting}
                    >
                        {submitting ? 'Generando...' : 'Generar PDFs'}
                    </Button>
                </div>


            </div>

        </Modal>
        
    )
}

export default ExportByDatesModal;